/**
 * RunOperationsTrail — one run's multi-stage story on the run page (FULL-0113 / PROD-OPSUX2-027).
 *
 * The rendering half of `runOperationsTrailModel.js`. MASTER_FLOW §15A names the missing surface it
 * completes: a run timeline showing tool calls, approvals, outcomes, errors, latency/cost and
 * verification evidence, without hidden chain-of-thought or secrets. The run page already held these
 * facts in four separate cards; an operator had to reassemble the story. This component puts them in
 * the order the work happened — analysis, proposal, approval, execution, verification — and keeps
 * every detailed card below as the drill-down.
 *
 * READ-ONLY BY CONSTRUCTION. It renders no button, input, checkbox, combobox or form, mints no token
 * and calls no mutating endpoint: the only API call it makes is the read-only `qaInspection` on the
 * same `agents_view` grant and the same query key as `RunQaInspectorPanel`. Every decision is still
 * taken in the controls above, on the surface that owns it.
 *
 * The words come from the shared vocabularies, never from a local map: `runStatusLabel`,
 * `actionTypeLabel`, `qaAnalysisRiskLabel`, `qaActionOutcomeLabel`, `riskGradeLabel`,
 * `evidenceSourceLabel` and the `runQaVocabulary` readers. The QA stage copies the server's verdict,
 * closure and missing-evidence arms; it never re-grades them.
 */
import { useQuery } from "@tanstack/react-query";

import { agentRunsApi } from "../services/api";
import { parseUtc } from "../utils/utcDate";
import { actionTypeLabel } from "./actionTypeVocabulary";
import { degradedAnalysisLabel } from "./degradedAnalysisVocabulary";
import { riskGradeLabel } from "./riskGradeVocabulary";
import { runStatusLabel } from "./runStatusVocabulary";
import {
  evidenceSourceLabel,
  qaActionOutcomeLabel,
  qaAnalysisRiskLabel,
  qaAuthorityNotice,
  qaClosureReasonText,
  qaSlotLabel,
  qaSlotStatusMeta,
  qaVerdictMeta,
  qaVerdictReasonText,
} from "./runQaVocabulary";
import { buildRunTrail, initiatedByLabel, RUN_TRAIL_STAGES } from "./runOperationsTrailModel";

const STAGE_META = new Map(RUN_TRAIL_STAGES.map((stage) => [stage.key, stage]));

function fmtDate(value) {
  if (!value) return null;
  const parsed = parseUtc(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat("he-IL", { dateStyle: "short", timeStyle: "short" }).format(parsed);
}

function fmtDuration(ms) {
  if (ms == null) return "לא דווח";
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function numberFact(value) {
  return value == null ? "לא דווח" : String(value);
}

function actionsCountText(count) {
  return count === 1 ? "פעולה אחת מתועדת" : `${count} פעולות מתועדות`;
}

function failuresCountText(count) {
  if (count === 0) return "לא נכשלה אף פעולה";
  return count === 1 ? "פעולה אחת נכשלה" : `${count} פעולות נכשלו`;
}

// The arm is a word in the data, never a word on the screen: the visible marker is Hebrew, and the
// raw arm stays in the `data-stage-arm` attribute for tests and styling.
const ARM_LABEL = {
  absent: "לא נרשם",
  empty: "נרשם ללא תוכן",
  unreadable: "לא קריא",
  present: "נרשם",
  recorded: "נרשם",
  unreported: "לא דווח",
  loading: "טוען",
  unavailable: "לא ניתן לקרוא",
};

function StageHeader({ index, meta, arm }) {
  return (
    <>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="text-[10px] font-mono text-gray-400">שלב {index}</span>
        <h3 className="text-sm font-semibold text-gray-700">{meta.title}</h3>
        <span data-testid={`run-trail-stage-status-${meta.key}`} className="text-[10px] text-gray-400" data-stage-arm={arm}>
          {ARM_LABEL[arm] || "מצב לא מזוהה"}
        </span>
      </div>
      <p className="text-[11px] text-gray-400">{meta.description}</p>
    </>
  );
}

function Stage({ stage, children }) {
  const meta = STAGE_META.get(stage.key);
  const index = RUN_TRAIL_STAGES.findIndex((entry) => entry.key === stage.key) + 1;
  return (
    <li
      data-testid={`run-trail-stage-${stage.key}`}
      data-stage-arm={stage.arm}
      className="relative mr-5 space-y-1.5"
    >
      <span className="absolute -right-[1.45rem] mt-1 h-2.5 w-2.5 rounded-full border-2 border-gray-300 bg-white" />
      <StageHeader index={index} meta={meta} arm={stage.arm} />
      <div className="space-y-1">{children}</div>
    </li>
  );
}

function AnalysisStage({ stage }) {
  if (stage.arm === "absent") {
    return (
      <Stage stage={stage}>
        <p className="text-sm text-gray-400">לא נרשם ניתוח לריצה זו.</p>
      </Stage>
    );
  }
  if (stage.arm === "unreadable") {
    return (
      <Stage stage={stage}>
        <p role="alert" className="text-xs text-amber-700">
          הניתוח נרשם במבנה שאינו קריא — אין להסיק ממנו מסקנה.
        </p>
      </Stage>
    );
  }
  if (stage.arm === "empty") {
    return (
      <Stage stage={stage}>
        <p className="text-sm text-gray-400">הניתוח נרשם ללא תוכן.</p>
      </Stage>
    );
  }
  const degraded = degradedAnalysisLabel(stage.degraded);
  return (
    <Stage stage={stage}>
      {degraded && (
        <p role="alert" data-testid="run-trail-degraded" className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-1">
          {degraded}
        </p>
      )}
      <p className="text-sm text-gray-700">{stage.summary || "הניתוח נרשם ללא סיכום."}</p>
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
        <span data-testid="run-trail-confidence">
          ביטחון: {stage.confidence == null ? "לא דווח" : `${stage.confidence}%`}
        </span>
        {stage.riskLevel && (
          <span data-testid="run-trail-analysis-risk">דרגת סיכון: {qaAnalysisRiskLabel(stage.riskLevel)}</span>
        )}
      </div>
      {stage.evidenceSources.length > 0 ? (
        <div data-testid="run-trail-evidence" className="flex flex-wrap gap-1">
          {stage.evidenceSources.map((source, index) => (
            <span key={`${source}-${index}`} className="text-[11px] bg-gray-100 text-gray-600 rounded px-1.5 py-0.5">
              {evidenceSourceLabel(source)}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-gray-400">לא הוצהרו מקורות ראיה.</p>
      )}
    </Stage>
  );
}

function ProposalStage({ stage }) {
  if (stage.arm === "absent" || stage.arm === "empty") {
    return (
      <Stage stage={stage}>
        <p className="text-sm text-gray-400">לא הוצעה פעולה.</p>
      </Stage>
    );
  }
  return (
    <Stage stage={stage}>
      <p className="text-sm text-gray-700">{stage.description || "הפעולה המוצעת נרשמה ללא תיאור."}</p>
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
        {stage.actionType && (
          <span data-testid="run-trail-proposal-type" className="bg-blue-50 text-blue-700 border border-blue-200 rounded px-1.5 py-0.5">
            {actionTypeLabel(stage.actionType)}
          </span>
        )}
        {stage.target && <span data-testid="run-trail-proposal-target">יעד: {stage.target}</span>}
      </div>
    </Stage>
  );
}

const DECISION_TEXT = {
  "human-decision": "נדרשת החלטה אנושית — אישור או דחייה ניתנים בפקדים שלמעלה, לא במסלול הזה.",
  resumable: "הריצה מושהית — חידוש ניתן בפקדים שלמעלה. הריצה אינה ממתינה לאישור.",
  none: "אין החלטה אנושית פתוחה לריצה הזו.",
};

function ApprovalTokenRow({ token, index }) {
  const approvedAt = fmtDate(token.approvedAt);
  const expiresAt = fmtDate(token.expiresAt);
  const tone = token.state === "revoked"
    ? "border-red-200 bg-red-50"
    : token.state === "used"
      ? "border-gray-200 bg-gray-50"
      : token.state === "expired"
        ? "border-amber-200 bg-amber-50"
        : token.state === "active"
          ? "border-green-200 bg-green-50"
          : "border-gray-200 bg-white";
  return (
    <li
      data-testid={`run-trail-token-${token.id || index}`}
      data-token-state={token.state}
      className={`rounded-lg border p-2 text-xs space-y-0.5 ${tone}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-gray-700">
          {token.actionType ? actionTypeLabel(token.actionType) : "סוג פעולה לא דווח"}
        </span>
        <span className="px-1.5 py-0.5 rounded bg-white/70 border border-gray-200 text-[10px]">{token.stateLabel}</span>
        <span className="text-[10px] text-gray-500">דרגת סיכון: {riskGradeLabel(token.riskLevel)}</span>
      </div>
      <div className="flex flex-wrap gap-3 text-[11px] text-gray-500">
        {token.approverId && <span>מאשר: {token.approverId}</span>}
        {approvedAt && <span>אושר: {approvedAt}</span>}
        {expiresAt && <span>תפוגה: {expiresAt}</span>}
        {token.target && <span>יעד: {token.target}</span>}
      </div>
    </li>
  );
}

function ApprovalStage({ stage }) {
  return (
    <Stage stage={stage}>
      <p className="text-xs text-gray-600">{DECISION_TEXT[stage.decision]}</p>
      <p className="text-[11px] font-medium text-gray-600">אסימוני אישור</p>
      {stage.arm === "loading" && <p className="text-xs text-gray-400">טוען אסימוני אישור…</p>}
      {stage.arm === "unreported" && (
        <p role="alert" className="text-xs text-amber-700">
          לא דווחו אסימוני אישור — לא ידוע, ולא אפס.
        </p>
      )}
      {stage.arm === "empty" && (
        <p className="text-xs text-gray-400">לא נרשמו אסימוני אישור לריצה זו.</p>
      )}
      {stage.arm === "recorded" && (
        <ul data-testid="run-trail-tokens" className="space-y-1">
          {stage.tokens.map((token, index) => (
            <ApprovalTokenRow key={token.id || `token-${index}`} token={token} index={index} />
          ))}
        </ul>
      )}
    </Stage>
  );
}

function ExecutionStage({ stage }) {
  if (stage.arm === "unreported") {
    return (
      <Stage stage={stage}>
        <p role="alert" className="text-xs text-amber-700">
          לא דווחו פעולות לריצה זו — לא ידוע, ולא אפס.
        </p>
      </Stage>
    );
  }
  if (stage.arm === "empty") {
    return (
      <Stage stage={stage}>
        <p className="text-sm text-gray-400">אין פעולות מתועדות.</p>
      </Stage>
    );
  }
  return (
    <Stage stage={stage}>
      <p className="text-[11px] text-gray-500">
        {actionsCountText(stage.actions.length)} · {failuresCountText(stage.failedCount)}
      </p>
      <ol data-testid="run-trail-actions" className="space-y-1.5">
        {stage.actions.map((action, index) => (
          <li
            key={action.id || `action-${index}`}
            data-testid={`run-trail-action-${action.id || index}`}
            data-action-outcome={action.outcome || "unknown"}
            data-action-failed={action.failed ? "true" : "false"}
            className={`rounded-lg border px-2.5 py-2 text-xs space-y-0.5 ${
              action.failed ? "border-red-200 bg-red-50/60" : "border-gray-100 bg-gray-50/60"
            }`}
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[10px] text-gray-400">
                {action.sequenceNo == null ? "ללא מספר סידור" : `#${action.sequenceNo}`}
              </span>
              {action.actionType && (
                <span className="bg-gray-100 text-gray-600 rounded px-1.5 py-0.5">{actionTypeLabel(action.actionType)}</span>
              )}
              <span className={`font-medium ${action.failed ? "text-red-700" : "text-gray-600"}`}>
                {qaActionOutcomeLabel(action.outcome)}
              </span>
              <span className="text-[10px] text-gray-500">דרגת סיכון: {qaAnalysisRiskLabel(action.riskLevel)}</span>
              <span className="text-[10px] text-gray-400">{fmtDuration(action.durationMs)}</span>
            </div>
            <p className="text-sm text-gray-700">{action.description || "ללא תיאור"}</p>
            {fmtDate(action.executedAt) && (
              <p className="text-[10px] text-gray-400">בוצע: {fmtDate(action.executedAt)}</p>
            )}
          </li>
        ))}
      </ol>
    </Stage>
  );
}

function VerificationStage({ stage, query }) {
  if (query.isLoading) {
    return (
      <Stage stage={{ ...stage, arm: "loading" }}>
        <p className="text-xs text-gray-400">טוען את אימות הראיות…</p>
      </Stage>
    );
  }
  if (query.isError) {
    return (
      <Stage stage={{ ...stage, arm: "unavailable" }}>
        <p role="status" className="text-xs bg-gray-50 border border-gray-200 text-gray-600 rounded px-2 py-1">
          לא ניתן לקרוא את אימות הראיות מהשרת — אין כאן מסקנת אימות, ואין להסיק שהריצה נקייה.
        </p>
      </Stage>
    );
  }
  if (stage.arm === "unreadable") {
    return (
      <Stage stage={stage}>
        <p role="alert" className="text-xs text-amber-700">
          אימות הראיות נרשם במבנה שאינו קריא — אין להסיק ממנו סיווג.
        </p>
      </Stage>
    );
  }
  if (stage.arm !== "recorded") {
    return (
      <Stage stage={stage}>
        <p role="status" className="text-xs bg-gray-50 border border-gray-200 text-gray-600 rounded px-2 py-1">
          לא נרשם אימות ראיות לריצה זו — אין כאן סיווג, ואין להסיק שהריצה נקייה.
        </p>
      </Stage>
    );
  }

  const verdict = qaVerdictMeta(stage.verdict);
  const authority = qaAuthorityNotice(stage.authority);
  const generatedAt = fmtDate(stage.generatedAt);
  return (
    <Stage stage={stage}>
      <div
        role={verdict.alarm ? "alert" : "status"}
        data-testid="run-trail-verdict"
        className={`text-xs rounded px-2 py-1 border ${verdict.cls}`}
      >
        <div className="font-medium">{verdict.label}</div>
        <div>{qaVerdictReasonText(stage.verdictReason)}</div>
        <div className="text-[10px] opacity-80">{verdict.hint}</div>
      </div>
      {stage.closure && (
        <p data-testid="run-trail-closure" className="text-xs text-gray-600">
          <span className="font-medium text-gray-700">
            {stage.closure.eligible ? "ניתן לסגור ללא קריאה חוזרת" : "לא ניתן לסגור ללא קריאה חוזרת"}
          </span>
          {" — "}
          {qaClosureReasonText(stage.closure.reason)}
        </p>
      )}
      <p data-testid="run-trail-findings" className="text-[11px] text-gray-500">
        {stage.findingsCount == null ? "מספר הממצאים לא דווח" : `ממצאים: ${stage.findingsCount}`}
      </p>
      {stage.missing.length > 0 && (
        <div data-testid="run-trail-missing" className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-1">
          <p className="font-medium">ראיות חסרות</p>
          <ul className="list-disc ps-5">
            {stage.missing.map((entry, index) => (
              <li key={`${entry.kind || "missing"}-${index}`}>
                {qaSlotLabel(entry.kind)} — {qaSlotStatusMeta(entry.status).label}
              </li>
            ))}
          </ul>
        </div>
      )}
      <p
        data-testid="run-trail-verification-authority"
        className={`text-[10px] ${authority.alarm ? "text-red-700 font-medium" : "text-gray-400"}`}
      >
        {authority.text}
      </p>
      <p className="text-[10px] text-gray-400">
        הפירוט המלא — קלטי ההחלטה, המקורות והממצאים — בבקרת איכות הריצה שלמטה.
        {generatedAt ? ` נבדק: ${generatedAt}.` : ""}
      </p>
    </Stage>
  );
}

function TelemetryFacts({ telemetry }) {
  const tokenPair = [telemetry.inputTokens, telemetry.outputTokens, telemetry.cachedTokens];
  const tokensText = tokenPair.every((value) => value == null)
    ? "לא דווח"
    : `${numberFact(telemetry.inputTokens)} / ${numberFact(telemetry.outputTokens)} / ${numberFact(telemetry.cachedTokens)}`;
  const model = [telemetry.provider, telemetry.model].filter(Boolean).join(" / ") || "לא דווח";
  const initiated = initiatedByLabel(telemetry.initiatedBy);
  const cost = telemetry.estimatedCost == null
    ? "לא דווח"
    : `$${telemetry.estimatedCost.toFixed(6)} ${telemetry.currency || "USD"}`;
  return (
    <section data-testid="run-trail-facts" aria-label="זמן ועלות" className="border-t border-gray-100 pt-2 space-y-1">
      <h3 className="text-xs font-semibold text-gray-700">זמן ועלות</h3>
      <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-[11px]">
        <div>
          <dt className="text-gray-500">משך</dt>
          <dd data-testid="run-trail-duration" className="text-gray-800">{fmtDuration(telemetry.durationMs)}</dd>
        </div>
        <div>
          <dt className="text-gray-500">קריאות כלים</dt>
          <dd data-testid="run-trail-tool-calls" className="text-gray-800">{numberFact(telemetry.toolCalls)}</dd>
        </div>
        <div>
          <dt className="text-gray-500">טוקנים (קלט / פלט / מטמון)</dt>
          <dd data-testid="run-trail-tokens-count" className="text-gray-800">{tokensText}</dd>
        </div>
        <div>
          <dt className="text-gray-500">עלות משוערת</dt>
          <dd data-testid="run-trail-cost" className="text-gray-800">{cost}</dd>
        </div>
        <div>
          <dt className="text-gray-500">ספק / מודל</dt>
          <dd data-testid="run-trail-provider" className="text-gray-800">{model}</dd>
        </div>
        {initiated && (
          <div>
            <dt className="text-gray-500">הופעל על ידי</dt>
            <dd data-testid="run-trail-initiated-by" className="text-gray-800">{initiated}</dd>
          </div>
        )}
        {telemetry.checkName && (
          <div>
            <dt className="text-gray-500">שם הבדיקה</dt>
            <dd data-testid="run-trail-check-name" className="text-gray-800">{telemetry.checkName}</dd>
          </div>
        )}
      </dl>
      <p className="text-[10px] text-gray-400">
        הערכים מגיעים מרשומת הריצה. "לא דווח" פירושו שהמקור לא סיפק את הנתון — הוא אינו אפס.
      </p>
    </section>
  );
}

export default function RunOperationsTrail({ run, approvalTokens, tokensLoading = false, now = Date.now() }) {
  const runId = typeof run?.id === "string" && run.id.trim() ? run.id : null;
  // The same key and the same read-only endpoint as RunQaInspectorPanel: React Query dedupes the
  // request, so the verification arm on this trail and the detailed panel cannot disagree.
  const query = useQuery({
    queryKey: ["run-qa-inspection", runId],
    queryFn: () => agentRunsApi.qaInspection(runId),
    enabled: Boolean(runId),
    retry: false,
  });

  if (!runId) return null;

  const trail = buildRunTrail({
    run,
    approvalTokens,
    tokensLoading,
    qaInspection: query.data,
    now,
  });
  const byKey = new Map(trail.stages.map((stage) => [stage.key, stage]));

  return (
    <section
      data-testid="run-operations-trail"
      aria-labelledby="run-trail-title"
      dir="rtl"
      className="bg-white rounded-xl border shadow-sm p-4 space-y-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="run-trail-title" className="text-sm font-semibold text-gray-700">מסלול הריצה</h2>
        <span data-testid="run-trail-status" className="text-[11px] px-2 py-0.5 rounded-full border bg-gray-100 text-gray-600 border-gray-200">
          {runStatusLabel(trail.runStatus)}
        </span>
      </div>
      <p className="text-[11px] text-gray-400">
        סדר השלבים של העבודה הזו — ניתוח, הצעה, אישור, ביצוע ואימות — מהרשומות שהשרת משליך. המסלול
        אינו מציג חשיבה פנימית של מודל, אינו מאשר, אינו מריץ ואינו משנה דבר; כל החלטה ניתנת בפקדים
        שלמעלה.
      </p>
      <ol className="relative border-r border-gray-200 mr-3 space-y-4">
        <AnalysisStage stage={byKey.get("analysis")} />
        <ProposalStage stage={byKey.get("proposal")} />
        <ApprovalStage stage={byKey.get("approval")} />
        <ExecutionStage stage={byKey.get("execution")} />
        <VerificationStage stage={byKey.get("verification")} query={query} />
      </ol>
      <TelemetryFacts telemetry={trail.telemetry} />
    </section>
  );
}
